import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types/user';
import { login as loginService, signup as signupService, logout as logoutService, getCurrentUser } from '../services/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  setUser: (user: User | null) => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if we have tokens
        const access_token = localStorage.getItem('access_token');
        const refresh_token = localStorage.getItem('refresh_token');
        
        if (!access_token && !refresh_token) {
          setUser(null);
          setLoading(false);
          return;
        }

        // Try to get current user from API
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          // Update stored user data
          localStorage.setItem('user', JSON.stringify(currentUser));
        } else {
          // If no valid session, clear everything
          setUser(null);
          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      } catch (err) {
        setError('Failed to check session');
        // Clear any invalid stored data
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const user = await loginService({ email, password });
      setUser(user);
      // Store user data
      localStorage.setItem('user', JSON.stringify(user));
    } catch (err) {
      setError('Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string, phone?: string) => {
    try {
      setError(null);
      setLoading(true);
      const user = await signupService({ email, password, name, phone });
      setUser(user);
      // Store user data
      localStorage.setItem('user', JSON.stringify(user));
    } catch (err) {
      setError('Failed to create account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setLoading(true);
      await logoutService();
      setUser(null);
      // Clear all stored data
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch (err) {
      setError('Failed to logout');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, error, setUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 